/**
 * DashboardExportModal — PDF / PNG download dialog for dashboards.
 *
 * Pairs with the captureDashboardPdf / captureDashboardPng helpers in
 * analytics-shared/utils/dashboardCapture.js. The capture is
 * client-side (html2canvas + jspdf) — the user is already looking at
 * the rendered dashboard, so capturing the live DOM produces a
 * pixel-faithful screenshot. Server-side rendering would need
 * Playwright; deliberately deferred.
 *
 * The modal is render-target-agnostic: callers pass a ref to the
 * dashboard canvas root (or any DOM node). When `getNode` isn't
 * provided, the modal falls back to looking for
 * `[data-dashboard-export-root]` in the document — that anchor lives
 * on the react-grid-layout container inside DashboardCanvas, which
 * is the *only* element whose `scrollHeight` reflects the full
 * dashboard layout (RGL sets its container's height inline based on
 * widget positions). Anchoring on the page wrapper instead would
 * include the topbar/breadcrumb/filter bar in the capture AND clip
 * any widget scrolled off-screen — exactly the bug we just fixed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  FileImage, FileType, Loader2, X, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Export catalog — single source of truth for cards + dispatch keys.
const FORMATS = [
  {
    key: 'pdf',
    label: 'PDF',
    icon: FileType,
    accent: 'red',
    description: 'Multi-page A4 landscape PDF with the live dashboard layout.',
    detail: 'Charts, widgets, and grid positions are captured as they appear on screen.',
  },
  {
    key: 'png',
    label: 'Image (PNG)',
    icon: FileImage,
    accent: 'indigo',
    description: 'Full-canvas screenshot at retina resolution.',
    detail: 'Ideal for embedding in slides or sharing in chat.',
  },
];

const ACCENT_CLS = {
  red: {
    border: 'border-red-200', icon: 'text-red-600 bg-red-50',
  },
  indigo: {
    border: 'border-indigo-200', icon: 'text-indigo-600 bg-indigo-50',
  },
};

const DEFAULT_NODE_SELECTOR = '[data-dashboard-export-root]';

const DashboardExportModal = ({
  open,
  onClose,
  dashboard,
  // getNode() returns the DOM element to capture. Falls back to the
  // data-attribute selector on document.body — see file header.
  getNode,
}) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clean transient state on every open so a retry after error starts
  // from a fresh canvas.
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  const handleOpenChange = (v) => {
    if (!v && !busy) onClose();
  };

  const resolveNode = useCallback(() => {
    if (typeof getNode === 'function') {
      const n = getNode();
      if (n) return n;
    }
    return document.querySelector(DEFAULT_NODE_SELECTOR);
  }, [getNode]);

  const handleExport = useCallback(async (formatKey) => {
    if (busy) return; // dedupe duplicate clicks
    const node = resolveNode();
    if (!node) {
      const msg = 'Dashboard canvas not found. Try refreshing the page.';
      setError(msg); toast.error(msg);
      return;
    }
    setBusy(formatKey);
    setError(null);
    setSuccess(null);

    try {
      // Dynamic import: keeps html2canvas + jspdf out of the page's
      // initial bundle until the user actually clicks Download.
      const capture = await import('../utils/dashboardCapture');
      const name = dashboard?.name || 'Dashboard';
      let size = 0;
      if (formatKey === 'pdf') {
        size = await capture.captureDashboardPdf(node, { name, title: name });
      } else {
        size = await capture.captureDashboardPng(node, { name });
      }
      const sizeKb = size ? Math.max(1, Math.round(size / 1024)) : null;
      setSuccess({ format: formatKey.toUpperCase(), sizeKb });
      toast.success(
        `${formatKey.toUpperCase()} export ready${sizeKb ? ` (${sizeKb} KB)` : ''}`,
      );
    } catch (e) {
      // html2canvas surfaces a bunch of edge cases (CORS-tainted
      // images on cross-origin avatars, ResizeObserver timing on
      // recharts containers). Surface the message instead of a
      // generic "Something went wrong".
      const msg = e?.message || `Failed to export ${formatKey.toUpperCase()}`;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  }, [busy, dashboard, resolveNode]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="text-lg text-slate-900 flex items-center gap-2 truncate">
            Download <span className="font-normal text-slate-500 truncate">"{dashboard?.name || 'Dashboard'}"</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Captures the live dashboard exactly as it's rendered — charts, widgets, grid layout, and your applied filters.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-2.5">
          {FORMATS.map(({ key, label, icon: Icon, accent, description, detail }) => {
            const cls = ACCENT_CLS[accent] || ACCENT_CLS.red;
            const isBusyHere = busy === key;
            const anyBusy = !!busy;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleExport(key)}
                disabled={anyBusy}
                className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl border bg-white transition-all hover:border-slate-300 ${cls.border} ${
                  anyBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'
                }`}
              >
                <div className={`p-2.5 rounded-lg flex-shrink-0 ${cls.icon}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{label}</span>
                    {isBusyHere && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{description}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{detail}</div>
                </div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide">
                  {isBusyHere ? 'Capturing…' : `.${key}`}
                </div>
              </button>
            );
          })}
        </div>

        {(error || success) && (
          <div className="px-6 pb-4">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200">
                <AlertTriangle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-rose-700 flex-1">{error}</div>
                <button onClick={() => setError(null)} className="text-rose-400 hover:text-rose-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {success && !error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <div className="text-xs text-emerald-800">
                  {success.format} downloaded{success.sizeKb ? ` (${success.sizeKb} KB)` : ''}.
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">
            Captured client-side — large dashboards may take a few seconds.
          </span>
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)} disabled={!!busy}>
            {busy ? 'Working…' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardExportModal;
